<template>
  <div
    class="bg-white border-0 d-flex justify-content-between align-items-center cursor-pointer"
    @click="toggle_tab('Forms')"
  >
  </div>

  <div class="collapse" :class="{ 'show': active_tabs.includes('Forms') }">
    <div class="mt-2">
      <div class="border-0 p-2 rounded-lg">
        
        <!-- Header -->
        <div class="compliance-monitoring-program-header">
          <div class="d-flex justify-content-between align-items-start w-100 mb-2">
            <div class="d-flex align-items-start">
              <div class="ms-2 mt-1">
                <i class="bx bx-folder-open text-dark-blue fs-4"></i>
              </div>
              <p class="mb-0 text-xs fw-bold ms-2 mt-2">View results for {{ form_name }}</p>
            </div>
          </div>
        </div>

        <!-- Services Card -->
        <div class="compliance-monitoring-projects-wrapper">
          

          <!-- Results Section -->
          <div
            class="compliance-monitoring-project-accordion border border-2 rounded-lg mb-3 ms-4"
            v-for="(result, index) in processed_verification_results"
            :key="result.key || index"
          >
            <!-- Card Header -->
            <div class="compliance-monitoring-project-header" @click="toggle_expanded(index)">
              <div class="compliance-monitoring-project-connector"></div>
              <div class="d-flex align-items-center">
                <i class="bi" :class="expanded_index === index ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
                <div class="ms-2">
                  <i class="bi bi-file-earmark-text fs-4"></i>
                </div>
                <div class="ms-3">
                  <div class="d-flex align-items-center gap-2 mb-1">
                    <p class="mb-0 text-xs">{{ result.label }}</p>
                    <span class="status-badge" :class="get_status_badge_class(result.status)">
                      {{ result.status }}
                    </span>
                  </div>
                  <p class="mb-0 text-xxs text-muted">{{ result.date }}</p>
                </div>
              </div>
            </div>

            <!--Field Results + Details -->
            <div class="w-100 px-3 pb-3 mt-3">
              <!-- FAIL CASE -->
              <div v-if="result.status === 'fail'" class="w-100">

                <!-- Alert -->
                <div
                  v-if="expanded_index === index"
                  class="alert alert-danger rounded-4 shadow-sm p-3 text-center mb-3"
                >
                  <i class="bi bi-x-circle me-2"></i>
                  <span class="fw-bold text-xs">
                    {{ result.message || 'System verification failed.' }}
                  </span>
                </div>

                <!--Field Results (standalone white card) -->
                <div
                  v-if="result.details && result.details.field_results"
                  class="card border-0 rounded-4 shadow-sm bg-white p-3 w-100 mb-3"
                >
                  <p class="fw-bold text-xs text-muted mb-3">Field Results</p>

                  <div
                    v-for="(field, key) in result.details.field_results"
                    :key="key"
                    class="field-result-item d-flex align-items-center justify-content-between mb-2"
                  >
                    <div class="d-flex align-items-center">
                      <i
                        class="me-2 fs-5"
                        :class="field.match ? 'bi bi-check-circle text-success' : 'bi bi-x-circle text-danger'"
                      ></i>
                      <span class="fw-semibold text-xs text-dark">{{ format_key(key) }}:</span>
                    </div>
                    <span
                      class="fw-bold text-xs text-end"
                      :class="field.match ? 'text-success' : 'text-danger'"
                    >
                      {{ field.user_answer }}
                    </span>
                  </div>
                </div>
              </div>

              <!--SUCCESS CASE -->
              <div
                v-else-if="result.status === 'success' && result.details && result.details.field_results"
                class="w-100"
              >
                <!--Field Results (standalone white card) -->
                <div class="card border-0 rounded-4 bg-white p-3 w-100 mb-0">
                  <div class="d-flex align-items-center justify-content-between mb-3">
                    <p class="fw-bold text-xs text-muted mb-0">Field Results</p>
                  </div>

                  <div
                    v-for="(field, key) in result.details.field_results"
                    :key="key"
                    class="field-result-item d-flex align-items-center justify-content-between mb-2"
                  >
                    <div class="d-flex align-items-center">
                      <i
                        class="me-2 fs-5"
                        :class="field.match ? 'bi bi-check-circle text-success' : 'bi bi-x-circle text-danger'"
                      ></i>
                      <span class="fw-semibold text-xs text-dark">{{ format_key(key) }}:</span>
                    </div>
                    <span
                      class="fw-bold text-xs text-end"
                      :class="field.match ? 'text-success' : 'text-danger'"
                    >
                      {{ field.user_answer }}
                    </span>
                  </div>
                </div>

                <!-- AML-Specific Display (UPDATED: shows dashboard even with 0 matches) -->
                <div
                  v-if="expanded_index === index && is_aml_service(result) && result.details.aml_check_results"
                  class="card border-0 rounded-4 bg-white p-4 w-100 mt-0">
                  
                  <div class="bg-light border rounded-3 p-3 mb-3">
                    <!-- Dashboard always shows when aml_check_results exists -->
                    <AMLDashboardResults
                      :aml="result.details.aml_check_results"
                      :date="result.date"
                    />
                    
                    <!-- Only show detailed matches if there are any -->
                    <div v-if="result.details.aml_check_results.total_matches > 0">
                      <p class="fw-bold text-xs text-dark mb-3">Detailed Matches</p>
                      <div
                        v-for="(match, idx) in result.details.aml_check_results.all_matches"
                        :key="idx"
                        class="card border mb-3 p-3 bg-white"
                      >
                        <div class="mb-3">
                          <h6 class="text-sm fw-bold mb-2">{{ match.entity_name }}</h6>
                          <span class="badge bg-success text-white text-xxs px-2 py-1 rounded">
                            {{ match.entity_type }}
                          </span>
                        </div>

                        <!-- Info Grid - Two Column Layout -->
                        <div class="row mb-3">
                          <div class="col-md-6 mb-2">
                            <div class="text-muted text-xxs mb-1">Category</div>
                            <div class="text-xs fw-normal">{{ match.category }}</div>
                          </div>

                          <div class="col-md-6 mb-2">
                            <div class="text-muted text-xxs mb-1">Date Listed</div>
                            <div class="text-xs fw-normal">{{ match.date_listed }}</div>
                          </div>

                          <div class="col-md-6 mb-2" v-if="match.gender !== 'N/A'">
                            <div class="text-muted text-xxs mb-1">Gender</div>
                            <div class="text-xs fw-normal">{{ match.gender }}</div>
                          </div>

                          <div class="col-md-6 mb-2" v-if="match.date_of_birth !== 'N/A'">
                            <div class="text-muted text-xxs mb-1">Date of Birth</div>
                            <div class="text-xs fw-normal">{{ match.date_of_birth }}</div>
                          </div>
                        </div>

                        <!-- Full Width Fields -->
                        <div v-if="match.occupation !== 'N/A'" class="mb-3">
                          <div class="text-muted text-xxs mb-1">Occupation</div>
                          <div class="text-xs fw-normal">{{ match.occupation }}</div>
                        </div>

                        <div class="mb-3">
                          <div class="text-muted text-xxs mb-1">Summary</div>
                          <div class="text-xs fw-normal">{{ match.summary }}</div>
                        </div>

                        <!-- Comments Section -->
                       <div v-if="match.comment" class="pt-3 border-top">
                        <div class="text-muted small mb-2 fw-semibold text-x">
                          References & Supporting Documents
                        </div>

                        <div class="row g-2">
                          <div
                            v-for="(ref, idx) in parse_references(match.comment)"
                            :key="idx"
                            class="col-md-6"
                          >
                            <!-- URL -->
                            <a 
                              v-if="ref.is_url"
                              :href="ref.raw"
                              target="_blank"
                              rel="noopener noreferrer"
                              class="d-flex gap-2 align-items-start p-2 rounded border bg-white text-info text-decoration-none w-100 text-xs"
                            >
                              <i class="bi bi-link-45deg flex-shrink-0"></i>
                              <span class="small text-break" style="line-height:1.4">{{ ref.display }}</span>
                            </a>

                            <!-- Text -->
                            <div v-else class="small bg-light border rounded p-2 text-dark text-xs" style="line-height:1.4">
                              {{ ref.display }}
                            </div>
                          </div>
                        </div>
                      </div>
                      </div>
                    </div>
                    
                    <!-- No Matches Message -->
                    <div v-else class="text-center py-4">
                      <i class="bi bi-check-circle text-success fs-1"></i>
                      <p class="text-muted mt-2 mb-0 text-xs">
                        No AML matches found. This is a positive result.
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Verification Details Card (shows for non-AML services only) -->
                <div
                  v-if="expanded_index === index && result.details.results_data && !is_aml_service(result)"
                  class="card border-0 rounded-4 bg-white p-4 w-100"
                >
                  <p class="fw-bold text-xs text-muted mb-3">Verification Details</p>

                  <!-- Loop through main results -->
                  <div
                    v-for="(value, key) in result.details.results_data"
                    :key="key"
                    class="bg-light border rounded-3 p-3 mb-3 border-0"
                  >
                    <p class="fw-bold text-xs text-dark mb-1">{{ format_key(key) }} :</p>

                    <!-- If nested object -->
                    <template v-if="typeof value === 'object' && value !== null">
                      <!-- Array -->
                      <div v-if="Array.isArray(value)">
                        <div
                          v-for="(item, i) in value"
                          :key="i"
                          class="border rounded-3 p-3 bg-light mb-2"
                        >
                          <div
                            v-for="(sub_value, sub_key) in item"
                            :key="sub_key"
                            class="mb-2"
                          >
                            <strong class="d-block text-xs text-muted">
                              {{ format_key(sub_key) }} :
                            </strong>
                            <span class="text-xs text-dark">
                              {{ format_value(sub_value) }}
                            </span>
                          </div>
                        </div>
                      </div>

                      <!-- Nested Object -->
                      <div v-else>
                        <div
                          v-for="(sub_value, sub_key) in value"
                          :key="sub_key"
                          class="border rounded-3 p-3 bg-white mb-2"
                        >
                          <strong class="d-block text-xs text-muted mb-1">
                            {{ format_key(sub_key) }} :
                          </strong>
                          <span class="text-xs text-dark">
                            {{ format_value(sub_value) }}
                          </span>
                        </div>
                      </div>
                    </template>

                    <!-- If simple value -->
                    <template v-else>
                      <p class="text-xs text-dark mb-0">{{ format_value(value) }}</p>
                    </template>
                  </div>
                </div>
              </div>
              </div>
            </div>
            <!-- Empty State -->
            <div
            v-if="processed_verification_results.length === 0 && selected_service_id && !loading_results"
            class="text-center p-4 border rounded-lg mt-3 ms-3 shadow"
          >
            <i class="bi bi-inbox fs-1 text-muted"></i>
            <p class="text-muted mt-2 mb-0 text-xs">
              No verification results found for {{ get_selected_service_name }}.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  GET_APPLICATION_FORMS,
  get_headers,
  GET_PROJECTS_ASSIGNED_TO_SERVICE,
} from '@/utils/constants/constants.js';
import axios from 'axios';
import { getKycVerificationResults } from "@/services/verification/integrated_verification_apis/kyc_verification";
import { getAMLVerificationResults } from "@/services/verification/integrated_verification_apis/aml_verification";
import { getIDVVerificationResults } from "@/services/verification/integrated_verification_apis/idv_verification";
import { getCIPCVerificationResults } from "@/services/verification/integrated_verification_apis/cipc_verification";
import { getCreditReportVerificationResults } from "@/services/verification/integrated_verification_apis/credit_report_verification";
import AMLDashboardResults from './AMLDashboardResults.vue';

export default {
  name: "VerificationResults",
  mixins: [alert_mixin],
  components: {
    AMLDashboardResults
  },
  props: {
    application_id: {
      type: [String, Number],
      default: null
    },
    entity_profile_id: {
      type: [String, Number],
      default: null
    },
    selected_service_id: {
      type: [String, Number],
      default: null
    },
    auto_fetch: {
      type: Boolean,
      default: true
    },
    external_results: {
      type: Array,
      default: () => []
    },
    // ADD LEAD PROPS
    lead_id: {
      type: [String, Number],
      default: null
    },
    lead_form_id: {
      type: [String, Number],
      default: null
    }
  },

  data: () => ({
    headers: get_headers(),
    expanded_index: null,
    active_tabs: ['Forms'],
    form_name: '',
    verification_services: [],
    api_verification_results: [],
    loading_results: false,
    loading_services: false,
    project_id: sessionStorage.getItem('project_id'),
  }),

  computed: {
    processed_verification_results() {
      const results = this.external_results.length > 0 ? this.external_results : this.api_verification_results;

      return results.map(result => ({
        key: result.key || result.label || result.service_name || `result-${Math.random()}`,
        label: result.label || result.service_name,
        date: result.date || result.timestamp || new Date().toLocaleDateString(),
        status: this.normalize_status(result.status),
        details: result.details || result.data || result,
        component: result.component,
      }));
    },

    resolved_entity_profile_id() {
      // Explicit props take highest priority
      if (this.entity_profile_id) return this.entity_profile_id;
      
      // Then check route params
      const route_entity_id = this.$route?.params?.entity_profile_id;
      if (route_entity_id) return route_entity_id;
      
      // Finally, check sessionStorage (only if no lead context)
      const entity_profile_id = sessionStorage.getItem('entity_profile_id');
      if (entity_profile_id) return entity_profile_id;
      
      return null;
    },
    
    resolved_lead_id() {
      // Only use lead_id if we don't have entity_profile_id
      const entity_profile_id = this.resolved_entity_profile_id;
      if (entity_profile_id) return null;
      
      // Explicit props take priority
      if (this.lead_id) return this.lead_id;
      
      // Then check route query
      const route_lead_id = this.$route?.query?.lead_id;
      if (route_lead_id) return route_lead_id;
      
      // Finally, check sessionStorage
      return sessionStorage.getItem('lead_id');
    },
    
    resolved_lead_form_id() {
      if (this.lead_form_id) return this.lead_form_id;
      return sessionStorage.getItem('lead_form_id');
    },
    
    is_lead_context() {
      return !!this.resolved_lead_form_id || !!this.resolved_lead_id;
    },

    get_selected_service_name() {
      const service_id = this.selected_service_id || this.$props.selected_service_id;
      if (!service_id) return '';
      const service = this.verification_services.find(s => s.id == service_id);
      return service ? service.name : `Service ${service_id}`;
    },

    selected_service() {
      const service_id = this.selected_service_id || this.$props.selected_service_id;
      if (!service_id) return null;
      return this.verification_services.find(s => s.id == service_id);
    }
  },

  async mounted() {
    await this.fetch_verification_services();
    
    if (this.application_id){
      this.get_forms();
    }
    
    if (this.auto_fetch) {
      this.fetch_verification_results();
    }
  },

  watch: {
    selected_service_id: {
      handler(new_val) {
        if (new_val && this.auto_fetch) {
          this.fetch_verification_results();
        }
      },
      immediate: true
    },
    
    // Watch for lead_id changes
    resolved_lead_id: {
      handler(new_val) {
        if (new_val && this.auto_fetch) {
          this.fetch_verification_results();
        }
      },
      immediate: true
    }
  },

  methods: {
    is_aml_service(result) {
      const label = (result.label || "").toLowerCase();
      return label.includes("aml") || label.includes("anti-money");
    },

    get_risk_alert_class(risk_score) {
      if (!risk_score) return "alert-secondary";
      const risk = risk_score.toLowerCase();
      if (risk.includes("high")) return "alert-danger";
      if (risk.includes("medium")) return "alert-warning";
      return "alert-success";
    },

    normalize_status(status) {
      if (!status) return "fail";
      const verification_status = status.toString().toLowerCase();

      if (["success", "pass", "verified", "ok"].includes(verification_status)) return "success";
      if (["fail", "failed", "error"].includes(verification_status)) return "fail";

      return verification_status; // fallback
    },

    is_nested_object(obj) {
      return (
        obj &&
        typeof obj === "object" &&
        !Array.isArray(obj) &&
        Object.keys(obj).length > 0
      );
    },

    filtered_detail_entries(obj) {
      if (!obj || typeof obj !== "object") return {};
      // Removes common meta keys that don't need to be displayed in the nested section
      return Object.fromEntries(
        Object.entries(obj).filter(([key]) => key !== "field_results" && key !== "message")
      );
    },

    async fetch_verification_services() {
      this.loading_services = true;
      try {
        const response = await axios.get(GET_PROJECTS_ASSIGNED_TO_SERVICE, {
          headers: this.headers,
          params: { project_id: this.project_id },
        });
        const parsed = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
        if (parsed.status === 'success' && parsed.data) {
          this.verification_services = parsed.data.map(service => ({
            id: service.id,
            name: service.name,
            description: service.description,
            is_active: service.is_active
          }));
        } else this.show_error(parsed.message || 'Failed to fetch services');
      } catch {
        this.show_error('Failed to load verification services.');
      } finally {
        this.loading_services = false;
      }
    },

    async on_service_change() {
      if (!this.selected_service_id) return;
      await this.fetch_verification_results();
    },

    // Fetch results for the selected service using its dedicated API
    async fetch_verification_results() {
      const entity_profile_id = this.resolved_entity_profile_id;
      const lead_id = this.resolved_lead_id;
      const service_id = this.selected_service_id || this.$props.selected_service_id;
      
      if ((!entity_profile_id && !lead_id) || !service_id)
        return;

      this.loading_results = true;
      this.api_verification_results = [];

      try {
        const service = this.selected_service;
        const name = service.name.toLowerCase();

        let api_call = null;

        // Dynamically pick the API based on the selected service
        if (name.includes("kyc")) {
          api_call = getKycVerificationResults;
        } else if (name.includes("aml")) {
          api_call = getAMLVerificationResults;
        } else if (name.includes("id")) {
          api_call = getIDVVerificationResults;
        } else if (name.includes("cipc")) {
          api_call = getCIPCVerificationResults;
        } else if (name.includes("credit")) {
          api_call = getCreditReportVerificationResults;
        }

        if (!api_call) {
          this.loading_results = false;
          return;
        }

        const params = {
          service_id: service_id,
        };

        if (entity_profile_id) {
          params.entity_profile_id = entity_profile_id;
        } else if (lead_id) {
          params.lead_id = lead_id;
        }

        // Call only the matching API
        const api_result = await api_call(params);

        // Process only this API's results
        if (api_result) {
          this.api_verification_results = this.process_single_api_response(
            api_result,
            service ? service.name : 'Verification',
            service ? service.name : 'Verification'
          );
        }
      } catch (error) {
        console.error("Error fetching verification results:", error);
      } finally {
        this.loading_results = false;
      }
    },

    process_all_api_responses(results, service_name) {
      const all_results = [];

      results.forEach((result, index) => {
        if (result.status === "fulfilled" && result.value) {
          const api_result = result.value;
          const api_names = ["KYC", "AML", "IDV", "CIPC", "Credit Report"];
          const api_name = api_names[index] || `API-${index + 1}`;

          if (["success", "fail"].includes(api_result.status)) {
            const processed_results = this.process_single_api_response(api_result, service_name, api_name);
            if (processed_results && processed_results.length > 0) all_results.push(...processed_results);
          }
        }
      });

      // Show only results that match the selected service
      this.api_verification_results = all_results.filter(result => {
        const selected = (this.selected_service?.name || "").toLowerCase();
        const label = (result.label || "").toLowerCase();
        return (
          label.includes(selected) ||
          selected.includes(label.split(" ")[0])
        );
      });
    },

    process_single_api_response(api_result, service_name, api_name) {
      if (api_result.results && api_result.results.length > 0) {
        return api_result.results.map((r, i) => ({
          key: `${api_name}-${this.selected_service_id}-${i}`,
          label: `${service_name} - ${api_name} Results`,
          date: new Date().toLocaleDateString(),
          status: r.status || api_result.status || "success",
          details: this.extract_details_from_result(r),
        }));
      }

      const details = this.extract_details_from_result(api_result.raw_data || api_result);
      return [{
        key: `${api_name}-${this.selected_service_id}-0`,
        label: `${service_name} - ${api_name} Results`,
        date: new Date().toLocaleDateString(),
        status: this.normalize_status(api_result.status),
        details,
      }];
    },

    extract_details_from_result(result_item) {
      // Directly return if it already has field_results or results_data
      if (result_item.results_data || result_item.field_results) {
        return result_item;
      }

      // If raw_data is nested inside result_item
      if (result_item.raw_data && (result_item.raw_data.results_data || result_item.raw_data.data)) {
        return {
          field_results: result_item.raw_data.field_results || {},
          results_data:
            result_item.raw_data.results_data ||
            result_item.raw_data.data ||
            result_item.raw_data.details ||
            {},
        };
      }

      // Fallback paths for inconsistent APIs
      if (result_item.data && (result_item.data.results_data || result_item.data.details)) {
        return {
          field_results: result_item.data.field_results || {},
          results_data:
            result_item.data.results_data ||
            result_item.data.details ||
            result_item.data ||
            {},
        };
      }

      // Final fallback
      return {
        field_results: result_item.field_results || {},
        results_data:
          result_item.results_data ||
          result_item.details ||
          result_item.data ||
          {},
      };
    },

    toggle_tab(tab) {
      this.active_tabs = this.active_tabs.includes(tab)
        ? this.active_tabs.filter(t => t !== tab)
        : [...this.active_tabs, tab];
    },

    toggle_expanded(index) { this.expanded_index = this.expanded_index === index ? null : index; },

    format_key(str) {
      if (typeof str !== 'string') return String(str || '');
      return str.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    format_value(value) {
      if (value === null || value === undefined) return "N/A";
      if (typeof value === "boolean") return value ? "Yes" : "No";
      if (typeof value === "object") return value; // keep as object for recursive render
      return String(value);
    },

    is_complex_object(val) {
      return val && typeof val === "object" && Object.keys(val).length > 0;
    },


    get_status_badge_class(status) {
      const s = (status || '').toLowerCase();
      const map = { success: 'success', fail: 'failed', error: 'failed', pending: 'pending' };
      return `status-badge ${map[s] || 'default'}`;
    },

    async get_forms() {
      if (!this.application_id) return;
      try {
        const { data: res } = await axios.get(GET_APPLICATION_FORMS, {
          headers: this.headers,
          params: { application_id: this.application_id },
        });
        const rows = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
          ? res.data.data
          : [];
        this.form_name = rows[0]?.form?.form_name ?? "";
      } catch (e) {
        this.show_error("Error loading forms: " + (e.response?.data?.message || e.message));
      }
    },

    parse_references(comment) {
      if (!comment) return [];

      return comment
        .split('|')
        .map(item => item.trim())
        .filter(Boolean)
        .map(item => {
          const is_url = /^https?:\/\//i.test(item);

          return {
            raw: item,
            is_url,
            display: is_url
              ? item.replace(/^https?:\/\//, '').slice(0, 60) + (item.length > 60 ? '…' : '')
              : item
          };
        });
    }
  },
};
</script>